import { ChangeEmitter } from '@/application/ChangeEmitter';
import { CuttingShapeController } from '@/application/controllers/CuttingShapeController';
import { EngineEventSubscriber } from '@/application/EngineEventSubscriber';
import { EntityController } from '@/application/controllers/EntityController';
import { ExportsController } from '@/application/controllers/ExportsController';
import { ImageController } from '@/application/controllers/ImageController';
import { PrintZoneController } from '@/application/controllers/PrintZoneController';
import { TextController } from '@/application/controllers/TextController';
import { ViewportController } from '@/application/controllers/ViewportController';
import { ZOrderController } from '@/application/controllers/ZOrderController';
import type { I2DEngine } from '@/application/ports/I2DEngine';
import type { I2DExporter } from '@/application/ports/I2DExporter';
import type { ICuttingShapeEngine } from '@/application/ports/ICuttingShapeEngine';
import type { ICuttingShapeRepository } from '@/application/ports/ICuttingShapeRepository';
import type { IEntityRepository } from '@/application/ports/IEntityRepository';
import type { IPrintZoneEngine2D } from '@/application/ports/IPrintZoneEngine2D';
import type { IProductImageRepository } from '@/application/ports/IProductImageRepository';
import type { IProductStateRepository } from '@/application/ports/IProductStateRepository';
import type { ISelectionState } from '@/application/ports/ISelectionState';
import type { ITemplateConfigurationRepository } from '@/application/ports/ITemplateConfigurationRepository';
import type { IViewportEngine2D } from '@/application/ports/IViewportEngine2D';
import type { TSide } from '@/domain/shared/TSide';

type TEditor2DControllerParams = {
    cuttingShapeEngine: ICuttingShapeEngine;
    cuttingShapeRepository: ICuttingShapeRepository;
    engine: I2DEngine;
    entityRepository: IEntityRepository;
    exporter: I2DExporter;
    printZoneEngine: IPrintZoneEngine2D;
    productImageRepository: IProductImageRepository;
    productStateRepository: IProductStateRepository;
    selection: ISelectionState;
    side: TSide;
    templateConfigurationRepository: ITemplateConfigurationRepository;
    viewportEngine: IViewportEngine2D;
};

class Editor2DController {
    private readonly _changeEmitter = new ChangeEmitter();
    private readonly _cuttingShapeController: CuttingShapeController;
    private readonly _engine: I2DEngine;
    private readonly _engineEventSubscriber: EngineEventSubscriber;
    private readonly _entityController: EntityController;
    private readonly _exportsController: ExportsController;
    private readonly _imageController: ImageController;
    private readonly _printZoneController: PrintZoneController;
    private readonly _selection: ISelectionState;
    private readonly _textController: TextController;
    private readonly _viewportController: ViewportController;
    private readonly _zOrderController: ZOrderController;

    constructor(params: TEditor2DControllerParams) {
        const {
            cuttingShapeEngine,
            cuttingShapeRepository,
            engine,
            entityRepository,
            exporter,
            printZoneEngine,
            productImageRepository,
            productStateRepository,
            selection,
            side,
            templateConfigurationRepository,
            viewportEngine,
        } = params;
        this._engine = engine;
        this._selection = selection;
        this._engineEventSubscriber = new EngineEventSubscriber(engine, entityRepository, selection, this._changeEmitter);
        this._zOrderController = new ZOrderController(engine, printZoneEngine, entityRepository, this._changeEmitter);
        this._entityController = new EntityController(engine, entityRepository, selection, this._changeEmitter, side, () =>
            this._zOrderController.enforceOverlayZOrder(),
        );
        this._imageController = new ImageController(engine, entityRepository, this._changeEmitter);
        this._textController = new TextController(engine, entityRepository, this._changeEmitter);
        this._viewportController = new ViewportController(engine, viewportEngine, this._changeEmitter);
        this._printZoneController = new PrintZoneController(
            printZoneEngine,
            templateConfigurationRepository,
            productStateRepository,
            productImageRepository,
            side,
            this._changeEmitter,
        );
        this._cuttingShapeController = new CuttingShapeController(
            cuttingShapeEngine,
            cuttingShapeRepository,
            productStateRepository,
            productImageRepository,
            side,
            this._changeEmitter,
        );
        this._exportsController = new ExportsController(exporter);
    }

    get cuttingShape(): CuttingShapeController {
        return this._cuttingShapeController;
    }

    get entity(): EntityController {
        return this._entityController;
    }

    get exports(): ExportsController {
        return this._exportsController;
    }

    get image(): ImageController {
        return this._imageController;
    }

    get printZone(): PrintZoneController {
        return this._printZoneController;
    }

    get text(): TextController {
        return this._textController;
    }

    get viewport(): ViewportController {
        return this._viewportController;
    }

    get zOrder(): ZOrderController {
        return this._zOrderController;
    }

    getActiveObjectAnchors():
        | { mb: { x: number; y: number }; ml: { x: number; y: number }; mr: { x: number; y: number }; mt: { x: number; y: number } }
        | undefined {
        return this._engine.getActiveObjectAnchors();
    }

    initLockedBackgroundGroup(): void {
        this._engine.initLockedBackgroundGroup();
    }

    isEditingActiveObject(): boolean {
        return this._engine.isEditingActiveObject();
    }

    onDidChange(callback: () => void): () => void {
        return this._changeEmitter.subscribe(callback);
    }

    renderNow(): void {
        this._engine.renderNow();
    }

    setAutoRender(enabled: boolean): void {
        this._engine.setAutoRender(enabled);
    }

    // Selection is two pieces of state: which entity, and which characters
    // inside it. Dropping only the entity leaves a stale text range behind, so
    // callers that mean "nothing is selected any more" go through this.
    clearSelection(): void {
        this._entityController.setSelected(undefined);
        this._selection.setSelectedTextRange(undefined);
    }

    dispose(): void {
        this._engineEventSubscriber.dispose();
        this._selection.setSelectedId(undefined);
        this._selection.setSelectedTextRange(undefined);
        this._engine.dispose();
    }
}

export { Editor2DController };
